import React from 'react';
import { useDraftState } from './builderDrafts';

/** Where an editor's value is kept while its draft is unsaved (builderDrafts.tsx): its draft key and the field's name. */
export interface DraftField { key: string; name: string }

/**
 * An editor's local value that follows its prop only when the prop's value changes. A re-render that brings the same
 * value (a refreshed row whose other fields changed) keeps whatever the teacher has typed but not saved yet. With
 * ``draft``, the value is kept in the page's draft store while unsaved: the editor opened again shows it.
 */
export function useSyncedState<T>(value: T, draft?: DraftField): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [state, setState] = useDraftState(draft?.key ?? null, draft?.name ?? '', value);
    const last = React.useRef(JSON.stringify(value ?? null));
    React.useEffect(() => {
        const next = JSON.stringify(value ?? null);
        if (next === last.current) return;
        last.current = next;
        setState(value);
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
    return [state, setState];
}

type Json = Record<string, unknown>;

/** The keys of an object whose values differ from another's (a key added or removed counts). */
function changedKeys(local: Json, from: Json): string[] {
    return [...new Set([...Object.keys(from), ...Object.keys(local)])].filter(key => JSON.stringify(local[key] ?? null) !== JSON.stringify(from[key] ?? null));
}

/** The saved object with the keys changed locally (since ``from``, the saved object the local one started from). */
function withChanges<T extends object>(saved: T, local: T, from: T): T {
    const result: Json = { ...(saved as Json) };
    for (const key of changedKeys(local as Json, from as Json)) {
        if (key in (local as Json)) result[key] = (local as Json)[key];
        else delete result[key];
    }
    return result as T;
}

/**
 * An editor's local copy of a saved JSON object that another editor of the same record also writes (an activity's
 * content, whose picture description its row editor saves): it follows the saved object key by key. When the saved
 * object changes, a key not changed here takes the saved value; one changed here keeps the teacher's. The third value
 * gives the object to save: the saved object as it is now, with only the keys changed here, so a save never writes back
 * an older value of a key the other editor saved meanwhile (whether or not this editor has re-rendered since). With
 * ``draft``, the local copy and the saved object it started from are kept in the page's draft store while unsaved.
 */
export function useSyncedObject<T extends object>(value: T, draft?: DraftField): [T, React.Dispatch<React.SetStateAction<T>>, (saved: T) => T] {
    // The local object together with the saved one it was made from: its changes are always taken against that one, also
    // while a newer saved object has arrived but is not merged into it yet.
    const [entry, setEntry] = useDraftState(draft?.key ?? null, draft?.name ?? '', () => ({ local: value, base: value }));
    const latest = React.useRef(entry);
    latest.current = entry;
    // A kept draft opened again merges a saved object newer than the one it started from, as for a change while open.
    const last = React.useRef(JSON.stringify(entry.base ?? null));
    React.useEffect(() => {
        const next = JSON.stringify(value ?? null);
        if (next === last.current) return;
        last.current = next;
        setEntry(current => ({ local: withChanges(value, current.local, current.base), base: value }));
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
    const setLocal = React.useCallback<React.Dispatch<React.SetStateAction<T>>>(action => setEntry(current => ({
        ...current, local: typeof action === 'function' ? (action as (previous: T) => T)(current.local) : action })), [setEntry]);
    return [entry.local, setLocal, saved => withChanges(saved, latest.current.local, latest.current.base)];
}
